import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import { useTranslation } from "../../hooks";
import { formatOtpDestination } from "../../types/otpChannel";
import { VerifyCodeIllustration } from "../shared/icons/BookingIllustrations";
export function OtpStep({ state, dispatch, onVerifyOtp, onSendOtp, onChangeNumber, }) {
    const { t } = useTranslation("appointment");
    const { otpChannel, countryCode, patientPhone, patientEmail, otpCode, otpSent, otpVerified, otpVerifying, otpError, } = state;
    const isEmail = otpChannel === "email";
    const destination = formatOtpDestination(otpChannel, {
        countryCode,
        phoneNumber: patientPhone,
        email: patientEmail,
    });
    const [resendTimer, setResendTimer] = useState(0);
    useEffect(() => {
        if (otpSent && !otpVerified)
            setResendTimer(90);
    }, [otpSent, otpVerified]);
    useEffect(() => {
        if (resendTimer > 0) {
            const interval = setInterval(() => setResendTimer((p) => p - 1), 1000);
            return () => clearInterval(interval);
        }
    }, [resendTimer]);
    const formatTimer = (s) => {
        const mins = Math.floor(s / 60);
        const secs = s % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };
    const hasAttemptedRef = useRef(false);
    const prevOtpRef = useRef("");
    useEffect(() => {
        if (prevOtpRef.current !== otpCode) {
            hasAttemptedRef.current = false;
            prevOtpRef.current = otpCode;
        }
    }, [otpCode]);
    useEffect(() => {
        if (otpCode.length === 6 &&
            otpSent &&
            !otpVerified &&
            !otpVerifying &&
            !otpError &&
            !hasAttemptedRef.current) {
            hasAttemptedRef.current = true;
            const timer = setTimeout(() => onVerifyOtp(), 300);
            return () => clearTimeout(timer);
        }
    }, [otpCode, otpSent, otpVerified, otpVerifying, otpError, onVerifyOtp]);
    return (_jsx(Box, { sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            p: 3,
            minHeight: 0,
            overflowY: "auto",
        }, children: _jsxs(Box, { sx: { maxWidth: 380, width: "100%" }, children: [_jsxs(Stack, { alignItems: "center", sx: { mb: 3 }, children: [_jsx(Box, { sx: { mb: 2, lineHeight: 0, color: "primary.main" }, children: _jsx(VerifyCodeIllustration, { width: 210 }) }), _jsx(Typography, { variant: "h6", sx: { mb: 0.5 }, children: t("enterVerificationCode") }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: isEmail
                                ? t("otpSentSubtitleEmail", "We sent a code to your email.")
                                : t("otpSentSubtitle") })] }), _jsxs(Stack, { spacing: 2, children: [_jsx(Chip, { icon: isEmail ? (_jsx(EmailIcon, { sx: { fontSize: 14 } })) : (_jsx(PhoneIcon, { sx: { fontSize: 14 } })), label: _jsxs(Stack, { direction: "row", alignItems: "center", spacing: 0.5, children: [_jsx("span", { style: { wordBreak: "break-all" }, children: destination }), _jsx(Link, { component: "button", variant: "body2", onClick: onChangeNumber, sx: { fontSize: 13 }, children: t("change") })] }), variant: "outlined", sx: { py: 2.5, justifyContent: "center" } }), _jsx(Box, { children: _jsx(OTPInput, { value: otpCode, onChange: (val) => {
                                    dispatch({ type: "SET_OTP_ERROR", payload: null });
                                    hasAttemptedRef.current = false;
                                    dispatch({ type: "SET_OTP_CODE", payload: val });
                                }, disabled: otpVerifying, isError: !!otpError }) }), otpError && !otpVerifying && (_jsx(Alert, { severity: "error", children: otpError })), otpVerifying && (_jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "center", spacing: 1.25, sx: { py: 1 }, children: [_jsx(CircularProgress, { size: 16 }), _jsx(Typography, { variant: "body2", sx: { fontWeight: 500 }, children: t("verifying") })] })), otpCode.length === 6 && !otpVerifying && otpError && (_jsx(Button, { variant: "contained", fullWidth: true, onClick: () => {
                                hasAttemptedRef.current = true;
                                onVerifyOtp();
                            }, children: t("retryVerification") })), !otpVerifying && (_jsxs(Typography, { variant: "body2", color: "text.secondary", sx: { textAlign: "center" }, children: [t("didntReceiveCode"), " ", _jsx(Link, { component: "button", variant: "body2", onClick: () => {
                                        onSendOtp();
                                        setResendTimer(90);
                                    }, disabled: state.otpSending || resendTimer > 0, sx: {
                                        fontWeight: 600,
                                        opacity: state.otpSending || resendTimer > 0 ? 0.6 : 1,
                                        cursor: state.otpSending || resendTimer > 0 ?
                                            "not-allowed"
                                            : "pointer",
                                    }, children: state.otpSending
                                        ? t("sending")
                                        : resendTimer > 0
                                            ? t("resendIn", undefined, { time: formatTimer(resendTimer) })
                                            : t("resend") })] }))] })] }) }));
}
function OTPInput({ value, onChange, disabled = false, isError = false, }) {
    const inputRefs = useRef([]);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const maxLength = 6;
    const handleChange = (index, digit) => {
        if (!/^\d*$/.test(digit))
            return;
        const newValue = value.split("");
        newValue[index] = digit;
        const result = newValue.join("").slice(0, maxLength);
        onChange(result);
        if (digit && index < maxLength - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };
    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !value[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && index > 0)
            inputRefs.current[index - 1]?.focus();
        if (e.key === "ArrowRight" && index < maxLength - 1)
            inputRefs.current[index + 1]?.focus();
    };
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, maxLength);
        onChange(pastedData);
        const focusIndex = Math.min(pastedData.length, maxLength - 1);
        inputRefs.current[focusIndex]?.focus();
    };
    return (_jsx(Stack, { direction: "row", justifyContent: "center", spacing: 1, sx: { width: "100%" }, children: Array.from({ length: maxLength }).map((_, index) => {
            const isFilled = !!value[index];
            const isFocused = focusedIndex === index;
            return (_jsx(Box, { component: "input", ref: (el) => {
                    inputRefs.current[index] = el;
                }, type: "text", inputMode: "numeric", autoComplete: "one-time-code", maxLength: 1, value: value[index] || "", onChange: (e) => handleChange(index, e.target.value), onKeyDown: (e) => handleKeyDown(index, e), onPaste: handlePaste, onFocus: () => setFocusedIndex(index), onBlur: () => setFocusedIndex(null), disabled: disabled, sx: {
                    flex: 1,
                    minWidth: 0,
                    height: { xs: 44, sm: 48 },
                    fontSize: 18,
                    fontWeight: 600,
                    textAlign: "center",
                    color: "text.primary",
                    bgcolor: "background.paper",
                    border: 1.5,
                    borderColor: isError ? "error.main"
                        : isFocused ? "primary.main"
                            : isFilled ? "primary.main"
                                : "divider",
                    borderRadius: 1.25,
                    outline: "none",
                    transition: "all 0.2s",
                    boxSizing: "border-box",
                    p: 0,
                    boxShadow: isFocused ?
                        (t) => `0 0 0 3px ${t.palette.primary.main}20`
                        : "none",
                    ...(isError && { bgcolor: "error.light" }),
                    ...(isFilled && !isError && { bgcolor: "primary.light" }),
                } }, index));
        }) }));
}
